import t from '../../../tools/t'
import { Column, Row } from './FlexContainer'

// Fraction below min
const START_FALLBACK_OF_MIN = 0.8 // -20%
// Fraction above max
const END_FALLBACK_OF_MAX = 1.2 // 20%
// Fraction above min
const END_FALLBACK_OF_MIN = 1.8 // 80%

interface BoundaryIndicatorProps {
    value: number
    min: number
    max?: number
    start?: number
    end?: number
    hideStrip?: boolean
}
export default function BoundaryIndicator({
    value,
    start,
    end,
    min,
    max,
    hideStrip = false,
}: BoundaryIndicatorProps) {
    const hasMax = !!max
    if (!start) start = min * START_FALLBACK_OF_MIN
    if (!end) end = max ? max * END_FALLBACK_OF_MAX : min * END_FALLBACK_OF_MIN
    if (!max) max = end
    if (min && max && start && end && (start > end || min > max || start > min || end < max))
        return console.log(
            'Boundaries provided to indicator cannot be rendered.',
            `\nStart: ${start}\nMin: ${min}\nMax: ${max}\nEnd: ${end}`
        ) as undefined
    return (
        <Column className="boundary-indicator" arrangement="spaced" grow tall>
            <Column arrangement="spaced" grow tall>
                <BoundaryLabel value={value} min={min} max={hasMax ? max : undefined} />
            </Column>
            {!hideStrip && (
                <BoundaryStrip value={value} start={start} end={end} min={min} max={max} />
            )}
        </Column>
    )
}

interface BoundaryValueProps {
    value: number
    min: number
    max?: number
}
interface BoundaryLabelProps extends BoundaryValueProps {}
const BoundaryLabel = ({ value, min, max }: BoundaryLabelProps) => {
    return (
        <Column>
            <Row gap={0.5} alignment="end">
                <div className="boundary-indicator__value">{value}</div>
                <div className="boundary-indicator__unit">{t('common_unit_kn')}</div>
            </Row>
            <Row className="boundary-indicator__range" gap={1.5}>
                <div className="boundary-indicator__min">
                    {t('common_min_unit_value', min.toString(), t('common_unit_kn'))}
                </div>
                {max && (
                    <div className="boundary-indicator__max">
                        {t('common_max_unit_value', max.toString(), t('common_unit_kn'))}
                    </div>
                )}
            </Row>
        </Column>
    )
}

interface BoundaryStripProps {
    value: number
    min: number
    max: number
    start: number
    end: number
}
const BoundaryStrip = ({ value, start, end, min, max }: BoundaryStripProps) => {
    const valid = value > 0
    const tooLow = min > 0 && value < start
    const tooHigh = max > 0 && value > end
    const inRange = !(tooLow || tooHigh)
    const minEqualsStart = min === start
    const maxEqualsEnd = max === end
    const length = end - start
    const percent = 100 / length
    const normalizedMin = (min - start) * percent
    const normalizedRange = (max - min) * percent
    const normalizedValue = (value - start) * percent

    return (
        <div className="boundary-indicator-strip">
            {!minEqualsStart && (
                <div
                    style={{ left: normalizedMin + '%' }}
                    className="boundary-indicator-strip__label boundary-indicator-strip__label--min"
                >
                    {t('common_min')}
                </div>
            )}
            <div
                className="boundary-indicator-strip__range"
                style={{
                    width: normalizedRange + '%',
                    marginLeft: normalizedMin + '%',
                }}
            />
            <div className="boundary-indicator-strip__line" />
            {valid && inRange && (
                <div
                    className="boundary-indicator-strip__value"
                    style={{ left: normalizedValue + '%' }}
                >
                    <div className="boundary-indicator-strip__value-container" />
                    <div className="boundary-indicator-strip__value-number">{value}</div>
                </div>
            )}
            {!maxEqualsEnd && (
                <div
                    style={{ left: normalizedMin + normalizedRange + '%' }}
                    className="boundary-indicator-strip__label boundary-indicator-strip__label--max"
                >
                    {t('common_max')}
                </div>
            )}
        </div>
    )
}
